import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import showToast from '@/components/ui/Toast';
import type { CustomFormField, CustomFormAnswerItem } from '@/services/communityService';
import LevelToggle from '@/components/community/badminton/LevelToggle';

interface CustomFormAnswerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customForm: CustomFormField[];
  onSubmit: (answers: CustomFormAnswerItem[]) => void;
  isLoading?: boolean;
}

export const CustomFormAnswerModal: React.FC<CustomFormAnswerModalProps> = ({
  isOpen,
  onClose,
  customForm,
  onSubmit,
  isLoading = false,
}) => {
  const [answers, setAnswers] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setAnswers({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    for (const field of customForm) {
      const val = (answers[field.id] || '').trim();
      if (field.required && !val) {
        showToast.error(`Pertanyaan "${field.title}" wajib diisi`);
        return;
      }
    }

    const result: CustomFormAnswerItem[] = customForm.map((field) => {
      const value = answers[field.id] !== undefined ? answers[field.id].trim() : '';
      return {
        field_id: field.id,
        question_id: field.id,
        title: field.title,
        type: field.type,
        template_type: field.template_type,
        value,
      };
    });

    onSubmit(result);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      position="bottom"
      hideCloseButton={true}
    >
      <div className="space-y-5 pb-2">
        {/* Header with Title, Close Button and Subtitle */}
        <div className="space-y-1 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-[#0E1726]">
              Data tambahan dari host
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors shrink-0 cursor-pointer"
              aria-label="Close modal"
            >
              <Icon icon="ph:x-bold" className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            Silakan lengkapi informasi berikut sesuai permintaan host acara sebelum bergabung.
          </p>
        </div>

        {/* Dynamic Fields Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-1">
            {customForm.map((field) => {
              const val = answers[field.id] || '';
              const isLevelField = field.template_type === 'level' || field.template === 'level';

              return (
                <div key={field.id} className="space-y-1.5">
                  {!isLevelField && (
                    <label className="block text-xs font-bold text-[#0E1726]">
                      {field.title}
                      {field.required ? (
                        <span className="text-red-500 ml-0.5">*</span>
                      ) : (
                        <span className="text-gray-400 font-normal ml-1">(Opsional)</span>
                      )}
                    </label>
                  )}

                  {isLevelField ? (
                    <div className="p-3.5 rounded-2xl bg-white border border-gray-200 space-y-2">
                      <span className="text-sm text-gray-500 block">
                        {field.title}
                        {field.required ? <span className="text-[#FC480D] ml-0.5">*</span> : null}
                      </span>
                      <LevelToggle
                        value={val}
                        options={field.options || []}
                        disabled={isLoading}
                        onChange={(next) => handleInputChange(field.id, next)}
                      />
                    </div>
                  ) : field.type === 'option' ? (
                    (field.options || []).length <= 4 ? (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {(field.options || []).map((opt, idx) => {
                          const isSelected = val === opt;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() =>
                                handleInputChange(field.id, isSelected ? '' : opt)
                              }
                              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                                isSelected
                                  ? 'bg-[#FC480D] border border-[#FC480D] text-white shadow-xs'
                                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <Icon
                                icon={isSelected ? 'ph:check-circle-fill' : 'ph:plus-circle-bold'}
                                className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-gray-400'}`}
                              />
                              <span>{opt}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="relative">
                        <select
                          value={val}
                          onChange={(e) => handleInputChange(field.id, e.target.value)}
                          className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-[#0E1726] focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors appearance-none pr-9 cursor-pointer"
                        >
                          <option value="">Pilih {field.title}</option>
                          {(field.options || []).map((opt, idx) => (
                            <option key={idx} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                        <Icon
                          icon="ph:caret-down-bold"
                          className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                        />
                      </div>
                    )
                  ) : field.type === 'date' ? (
                    <input
                      type="date"
                      value={val}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-[#0E1726] focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                    />
                  ) : field.type === 'number' ? (
                    <input
                      type="number"
                      value={val}
                      placeholder={`Masukkan ${field.title.toLowerCase()}`}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                    />
                  ) : (
                    <input
                      type="text"
                      value={val}
                      placeholder={`Masukkan ${field.title.toLowerCase()}`}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#FC480D] hover:bg-[#D83B08] text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 shadow-sm cursor-pointer"
            >
              {isLoading ? (
                <>
                  <Icon icon="ph:spinner-gap-bold" className="w-5 h-5 animate-spin" />
                  <span>Memproses...</span>
                </>
              ) : (
                <span>Simpan & Lanjutkan</span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CustomFormAnswerModal;
